import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import { IMG, WHATSAPP_LINK, CONTACT_EMAIL } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", dates: "", guests: "2", message: "" };

const fieldCls =
  "w-full border border-ivory/20 bg-charcoal/60 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-brass focus:outline-none transition-colors duration-300";

const STEPS = [
  { title: "Write to us", text: "Send the form, an email, or a WhatsApp message with your dates and party size." },
  { title: "We reply personally", text: "Bookings are taken offline. We confirm availability, answer questions, and plan your arrival." },
  { title: "You walk in", text: "We meet you at Bhasedi, and the last 3.5 km are walked through the forest — together." },
];

export default function PlanPage() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something interrupted the send. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="plan-page">
      <Seo
        title="Plan Your Stay | The Jamoon, Corbett"
        description="Plan your stay at The Jamoon in Corbett. Tell us your dates, interests and preferred experiences and let us help shape your wilderness journey."
        path="/plan"
        image={IMG.misty}
        imageAlt="Birdlife in the forest around The Jamoon in Corbett"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Plan Your Stay", path: "/plan" }]}
      />
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-36 sm:px-12 sm:pt-44 lg:px-20">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">Plan Your Stay</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-serif text-4xl font-light leading-tight tracking-tight text-ivory sm:text-5xl">
                Plan Your <span className="italic text-brass">Jamoon Journey</span>
              </h1>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-7 max-w-md text-base font-light leading-relaxed text-ivory/75">
                There is no booking engine here — deliberately. Every stay is arranged
                personally, the way the lodge runs everything else.
              </p>
            </Reveal>
            <div className="mt-12 space-y-0">
              {STEPS.map((s, i) => (
                <Reveal key={s.title} delay={0.1 + i * 0.08}>
                  <div data-testid={`plan-step-${i}`} className="flex gap-6 border-t border-ivory/10 py-6 last:border-b">
                    <span className="font-serif text-lg italic text-brass/70">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h2 className="font-serif text-xl font-light text-ivory">{s.title}</h2>
                      <p className="mt-2 text-sm leading-relaxed text-ivory/55">{s.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3}>
              <p className="mt-8 text-sm leading-relaxed text-ivory/55">
                Best time to visit: October to March. The final 3.5 km are walked — basic fitness
                is essential. Prefer to talk?{" "}
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="text-brass underline-offset-4 hover:underline">
                  WhatsApp us
                </a>{" "}
                or write to{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-brass underline-offset-4 hover:underline">
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="border border-brass/25 bg-forest p-8 sm:p-10">
              {done ? (
                <div className="py-10 text-center" data-testid="plan-success">
                  <p className="font-serif text-3xl text-ivory">The forest has your message.</p>
                  <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-ivory/70">
                    Thank you. We will write back personally, shortly. For a faster reply, reach
                    us on WhatsApp.
                  </p>
                  <a
                    data-testid="plan-success-whatsapp-btn"
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-7 inline-flex items-center gap-2 border border-brass/40 px-6 py-3 text-xs tracking-[0.2em] text-brass transition-colors duration-300 hover:bg-brass/10"
                  >
                    <MessageCircle className="h-4 w-4" /> CONTINUE ON WHATSAPP
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="plan-form">
                  <input data-testid="plan-name-input" required value={form.name} onChange={set("name")} placeholder="Full name" className={fieldCls} />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input data-testid="plan-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email" className={fieldCls} />
                    <input data-testid="plan-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" className={fieldCls} />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input data-testid="plan-dates-input" value={form.dates} onChange={set("dates")} placeholder="Preferred dates (e.g. mid-November)" className={fieldCls} />
                    <select data-testid="plan-guests-select" value={form.guests} onChange={set("guests")} className={fieldCls} aria-label="Number of guests">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <option key={n} value={n} className="bg-charcoal">
                          {n} {n === 1 ? "guest" : "guests"}
                        </option>
                      ))}
                    </select>
                  </div>
                  <textarea data-testid="plan-message-textarea" rows={5} value={form.message} onChange={set("message")} placeholder="Anything we should know — photography, birding, diet, the walk in…" className={fieldCls} />
                  <button
                    data-testid="plan-submit-btn"
                    type="submit"
                    disabled={sending}
                    className="w-full bg-brass py-4 text-xs font-semibold tracking-[0.25em] text-charcoal transition-colors duration-300 hover:bg-[#B89B6A] disabled:opacity-60"
                  >
                    {sending ? "SENDING…" : "SEND ENQUIRY"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Packages callout → dedicated page */}
      <section data-testid="plan-packages-callout" className="bg-forest py-20 sm:py-24">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-6 sm:px-12 md:flex-row md:items-center md:justify-between lg:px-20">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">Packages & Itineraries</p>
            <h2 className="mt-4 max-w-xl font-serif text-2xl font-light leading-tight text-ivory sm:text-3xl">
              Prefer a complete, guided plan? <span className="italic text-brass">See our two itineraries.</span>
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-ivory/60">
              The Jamoon Explorer and Corbett Discovery plans — 4 nights, fully arranged end to end.
            </p>
          </div>
          <Link
            data-testid="plan-view-packages-link"
            to="/packages"
            className="inline-flex flex-shrink-0 items-center gap-2 bg-brass px-7 py-4 text-xs font-semibold tracking-[0.25em] text-charcoal transition-colors duration-300 hover:bg-[#B89B6A]"
          >
            VIEW PACKAGES →
          </Link>
        </div>
      </section>

      <section className="relative flex h-[40vh] items-center justify-center overflow-hidden">
        <img src={IMG.misty} alt="Barbets at The Jamoon" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/55" />
        <p className="relative z-10 px-6 text-center font-serif text-2xl font-light italic text-ivory sm:text-3xl">
          Walk on the footsteps of Jim Corbett.
        </p>
      </section>
    </main>
  );
}
